import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { useThemeMode } from '../../app/providers/ThemeModeProvider';
import { useStableId } from './useStableId';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export type ConfirmDialogVariant = 'default' | 'delete' | 'cancel';

export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  onClose: () => void;
  onConfirm: () => void;
  darkState?: boolean;
  loading?: boolean;
}

const defaultTitles: Record<ConfirmDialogVariant, string> = {
  default: 'Confirmer l\'action',
  delete: 'Confirmer la suppression',
  cancel: 'Confirmer l\'annulation',
};

const defaultMessages: Record<ConfirmDialogVariant, string> = {
  default: 'Voulez-vous vraiment continuer ?',
  delete: 'Voulez-vous vraiment supprimer ?',
  cancel: 'Voulez-vous vraiment annuler ?',
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'default',
  onClose,
  onConfirm,
  darkState,
  loading = false,
}: ConfirmDialogProps) {
  const { darkState: themeDark } = useThemeMode();
  const resolvedDarkState = darkState ?? themeDark;
  const buttonColor = resolvedDarkState ? 'secondary' : 'primary';
  const resolvedTitle = title ?? defaultTitles[variant];
  const resolvedMessage = message ?? defaultMessages[variant];
  const titleId = useStableId('confirm-dialog-title');
  const descriptionId = useStableId('confirm-dialog-description');
  const isDestructive = variant === 'delete' || variant === 'cancel';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      role={isDestructive ? 'alertdialog' : 'dialog'}
      sx={{ zIndex: 1301 }}
    >
      <DialogTitle id={titleId}>{resolvedTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText id={descriptionId}>{resolvedMessage}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color={buttonColor}
          disabled={loading}
          autoFocus={isDestructive}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={buttonColor}
          autoFocus={!isDestructive}
          disabled={loading}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
